/**
 * ResizeHandle hook — drag to resize adjacent panels.
 *
 * Usage: <div phx-hook="ResizeHandle"
 *             data-direction="horizontal" data-target="previous|next"
 *             data-min="180" data-max="600"
 *             data-storage-key="..."        (optional: persist size to localStorage)
 *             data-css-var="--my-width">    (optional: drive a CSS var on <html>)
 *             data-target-selector="#panel"> (optional: explicit target; takes precedence)
 *
 * Default mode resizes the previous/next sibling's width/height via inline style.
 *
 * When `data-css-var` is set, the size is written to a CSS custom property on
 * <html> (document.documentElement) instead of the sibling's inline style. The
 * sibling must reference it, e.g. `w-[var(--my-width,20rem)]`. This is the
 * re-render-safe pattern (mirrors the Monica drawer): a JS-set inline style on
 * the panel gets clobbered when LiveView patches that subtree, but a var on
 * <html> — which LiveView never patches — survives, so the width is stable while
 * the panel's contents re-render (e.g. selecting a file in the test upload).
 *
 * When `data-storage-key` is set, the chosen size is persisted to localStorage
 * and restored on mount.
 */
export default {
  mounted() {
    const handle = this.el
    const direction = handle.dataset.direction || "horizontal"
    const targetSide = handle.dataset.target || "previous"
    const min = parseInt(handle.dataset.min || "150", 10)
    const max = parseInt(handle.dataset.max || "800", 10)
    const storageKey = handle.dataset.storageKey
    const cssVar = handle.dataset.cssVar
    const targetSelector = handle.dataset.targetSelector
    const sizeProp = direction === "horizontal" ? "width" : "height"
    const axisClientProp = direction === "horizontal" ? "clientX" : "clientY"
    const resizeClass = "resize-handle-resizing"
    const resizeDirectionClass = `resize-handle-resizing-${direction}`

    let startPos = 0
    let startSize = 0
    let target = null
    let pointerId = null
    let pendingSize = null
    let rafId = null
    let lastAppliedSize = null
    let dragShield = null
    let frozenIframes = []

    const getTarget = () => {
      if (targetSelector) return document.querySelector(targetSelector)
      return targetSide === "previous" ? handle.previousElementSibling : handle.nextElementSibling
    }

    const clamp = (n) => Math.min(max, Math.max(min, n))

    const syncAriaValue = (size) => {
      if (handle.getAttribute("role") === "separator") {
        handle.setAttribute("aria-valuenow", String(Math.round(clamp(size))))
      }
    }

    const readTargetSize = (t) => {
      if (!t) return 0
      const rect = t.getBoundingClientRect()
      return direction === "horizontal" ? rect.width : rect.height
    }

    const applySizeNow = (size, t) => {
      const nextSize = Math.round(clamp(size))
      lastAppliedSize = nextSize
      syncAriaValue(nextSize)

      if (cssVar) {
        document.documentElement.style.setProperty(cssVar, nextSize + "px")
      } else if (t) {
        t.style[sizeProp] = nextSize + "px"
        t.style.flexShrink = "0"
      }
    }

    const applySize = (size, t) => {
      pendingSize = size
      if (rafId) return

      rafId = window.requestAnimationFrame(() => {
        rafId = null
        applySizeNow(pendingSize, t)
        pendingSize = null
      })
    }

    const flushPendingSize = () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId)
        rafId = null
      }

      if (pendingSize != null) {
        applySizeNow(pendingSize, target)
        pendingSize = null
      }
    }

    const readSavedSize = () => {
      if (!storageKey) return null
      const saved = parseInt(localStorage.getItem(storageKey) || "", 10)
      return Number.isFinite(saved) ? saved : null
    }

    const persistSize = () => {
      if (!storageKey) return
      const size = lastAppliedSize || Math.round(readTargetSize(target))
      if (size) localStorage.setItem(storageKey, String(clamp(size)))
    }

    const resizeCursor = () => direction === "horizontal" ? "col-resize" : "row-resize"

    const freezeIframes = () => {
      frozenIframes = Array.from(document.querySelectorAll("iframe")).map((frame) => {
        const previous = frame.style.pointerEvents
        frame.style.pointerEvents = "none"
        return [frame, previous]
      })
    }

    const unfreezeIframes = () => {
      frozenIframes.forEach(([frame, previous]) => {
        frame.style.pointerEvents = previous
      })
      frozenIframes = []
    }

    const mountDragShield = () => {
      if (dragShield) return

      dragShield = document.createElement("div")
      dragShield.dataset.resizeHandleShield = "true"
      Object.assign(dragShield.style, {
        position: "fixed",
        inset: "0",
        zIndex: "2147483646",
        cursor: resizeCursor(),
        background: "transparent",
        pointerEvents: "auto",
        userSelect: "none",
        touchAction: "none"
      })
      document.body.appendChild(dragShield)
    }

    const unmountDragShield = () => {
      dragShield?.remove()
      dragShield = null
    }

    // Restore a persisted size on mount.
    if (storageKey) {
      const saved = readSavedSize()
      if (saved) applySizeNow(saved, getTarget())
    }

    if (lastAppliedSize == null) {
      syncAriaValue(readTargetSize(getTarget()))
    }

    const onKeyDown = (e) => {
      const firstArrow = direction === "horizontal" ? "ArrowLeft" : "ArrowUp"
      const secondArrow = direction === "horizontal" ? "ArrowRight" : "ArrowDown"

      if (![firstArrow, secondArrow, "Home", "End"].includes(e.key)) return

      const keyboardTarget = getTarget()
      if (!keyboardTarget) return

      e.preventDefault()

      const currentSize = readTargetSize(keyboardTarget)
      const step = e.shiftKey ? 48 : 24
      let nextSize

      if (e.key === "Home") {
        nextSize = min
      } else if (e.key === "End") {
        nextSize = max
      } else {
        const firstArrowDelta = targetSide === "next" ? step : -step
        nextSize = currentSize + (e.key === firstArrow ? firstArrowDelta : -firstArrowDelta)
      }

      applySizeNow(nextSize, keyboardTarget)

      if (storageKey) {
        localStorage.setItem(storageKey, String(lastAppliedSize))
      }
    }

    const onPointerDown = (e) => {
      if (e.button != null && e.button !== 0) return
      e.preventDefault()
      target = getTarget()
      if (!target) return

      pointerId = e.pointerId
      startPos = e[axisClientProp]
      startSize = readTargetSize(target)

      document.body.classList.add(resizeClass, resizeDirectionClass)
      freezeIframes()
      mountDragShield()
      try {
        handle.setPointerCapture?.(pointerId)
      } catch (_err) {
        // The drag shield still keeps iframe/canvas surfaces from stealing events.
      }

      window.addEventListener("pointermove", onPointerMove, { passive: false })
      window.addEventListener("pointerup", onPointerUp)
      window.addEventListener("pointercancel", onPointerUp)
    }

    const onPointerMove = (e) => {
      if (!target || (pointerId != null && e.pointerId !== pointerId)) return
      e.preventDefault()

      const diff = e[axisClientProp] - startPos
      const multiplier = targetSide === "next" ? -1 : 1
      applySize(clamp(startSize + diff * multiplier), target)
    }

    const onPointerUp = (e) => {
      if (pointerId != null && e.pointerId != null && e.pointerId !== pointerId) return

      flushPendingSize()
      persistSize()

      if (pointerId != null) {
        try {
          handle.releasePointerCapture?.(pointerId)
        } catch (_err) {
          // The pointer may already have been released after a cancel/lost capture.
        }
      }

      document.body.classList.remove(resizeClass, resizeDirectionClass)
      unmountDragShield()
      unfreezeIframes()
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)

      pointerId = null
      target = null
    }

    handle.addEventListener("pointerdown", onPointerDown)
    handle.addEventListener("keydown", onKeyDown)
    this._cleanup = () => {
      flushPendingSize()
      if (pointerId != null) {
        try {
          handle.releasePointerCapture?.(pointerId)
        } catch (_err) {
          // The pointer may already have been released after a patch/cancel.
        }
      }
      document.body.classList.remove(resizeClass, resizeDirectionClass)
      unmountDragShield()
      unfreezeIframes()
      handle.removeEventListener("pointerdown", onPointerDown)
      handle.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)
      pointerId = null
      target = null
    }
  },

  destroyed() {
    if (this._cleanup) this._cleanup()
  }
}
